/**
 * Page 1: login & passwordless onboarding (`/login`).
 *
 * Two paths share one form:
 * - new account: email → Brevo OTP → create a Passkey (PRF) → wrap a new Master Identity key;
 * - returning user: email → Passkey assertion → unwrap the stored device envelope.
 *
 * The actual ceremonies live in the shared SDK; this page only sequences them and renders state.
 */

import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "../auth/AuthContext";
import type { OtpTicket } from "../api";

type Mode = "signup" | "signin";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });
  const [mode, setMode] = useState<Mode>("signup");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [deviceName, setDeviceName] = useState("Primary Workstation Passkey");
  const [ticket, setTicket] = useState<OtpTicket | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const destination = search.redirect ?? "/account";

  const sendCode = async () => {
    setNotice(null);
    try {
      const issued = await auth.requestOtp(email);
      setTicket(issued.ticket);
      setPreview(issued.devOtpPreview ?? null);
      setNotice("Verification code sent. Enter it to create your Passkey.");
    } catch {
      // The provider sets `auth.error`; keep the page on the same step.
    }
  };

  const completeSignup = async () => {
    if (!ticket) return;
    try {
      await auth.completeSignup({ email, ticket, code, deviceName });
      await navigate({ to: destination });
    } catch {
      // `auth.error` renders below.
    }
  };

  const signIn = async () => {
    try {
      await auth.signIn(email);
      await navigate({ to: destination });
    } catch {
      // `auth.error` renders below.
    }
  };

  if (auth.session) {
    return (
      <main className="tj-container max-w-xl py-10" data-testid="login-page">
        <div className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-6">
          <h1 className="serif-heading text-2xl">You are signed in</h1>
          <p className="mt-2 text-sm text-[var(--tj-muted)]">
            {auth.session.email ?? "TensileAuth session"} · {auth.session.role ?? "member"}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/account" className="tj-btn tj-btn-primary inline-block">
              Account &amp; devices
            </Link>
            <Link to="/dashboard" className="tj-btn tj-btn-secondary inline-block">
              Dashboard
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="tj-container max-w-xl py-10" data-testid="login-page">
      <div className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-6">
        <span className="tj-badge tj-badge-green">TensileAuth SSO • rpId: tensilelabs.com</span>
        <h1 className="serif-heading mt-3 text-2xl">
          {mode === "signup" ? "Create your Tensile Labs Passkey" : "Sign in with your Passkey"}
        </h1>
        <p className="mt-1 text-sm text-[var(--tj-muted)]">
          Your Passkey unlocks Task Jinn and future Tensile Labs tools with end-to-end PKI envelope
          encryption.
        </p>

        <div className="mt-4 flex gap-2" role="tablist" aria-label="Authentication mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "signup"}
            className={`tj-btn flex-1 ${mode === "signup" ? "tj-btn-primary" : "tj-btn-secondary"}`}
            onClick={() => {
              setMode("signup");
              auth.clearError();
              setNotice(null);
            }}
          >
            New account (email code)
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "signin"}
            className={`tj-btn flex-1 ${mode === "signin" ? "tj-btn-primary" : "tj-btn-secondary"}`}
            onClick={() => {
              setMode("signin");
              auth.clearError();
              setNotice(null);
            }}
          >
            Existing Passkey
          </button>
        </div>

        <div aria-live="polite" className="mt-4 space-y-3">
          {auth.error ? (
            <p className="tj-badge tj-badge-red block rounded-md p-3" role="alert">
              {auth.error}
            </p>
          ) : null}
          {notice ? (
            <p className="tj-badge tj-badge-green block rounded-md p-3">{notice}</p>
          ) : null}
          {preview ? (
            <p className="rounded-md border border-dashed border-[var(--tj-border)] p-3 text-sm">
              Local development code: <code data-testid="otp-preview">{preview}</code>
            </p>
          ) : null}
        </div>

        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (mode === "signup") {
              if (ticket) void completeSignup();
              else void sendCode();
            } else {
              void signIn();
            }
          }}
        >
          <label className="text-sm font-semibold">
            Work or personal email
            <input
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2 font-normal"
            />
          </label>

          {mode === "signup" && ticket ? (
            <>
              <label className="text-sm font-semibold">
                Verification code
                <input
                  type="text"
                  name="otp_code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder="123456"
                  className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2 font-mono tracking-[0.2em]"
                />
              </label>
              <label className="text-sm font-semibold">
                Device name
                <input
                  type="text"
                  name="device_name"
                  required
                  value={deviceName}
                  onChange={(event) => setDeviceName(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2 font-normal"
                />
              </label>
            </>
          ) : null}

          <button type="submit" className="tj-btn tj-btn-primary" disabled={auth.busy}>
            {auth.busy
              ? "Working…"
              : mode === "signup"
                ? ticket
                  ? "Verify code & create Passkey"
                  : "Send verification code"
                : "Authenticate with Passkey"}
          </button>

          {mode === "signup" && ticket ? (
            <button
              type="button"
              className="text-xs text-[var(--tj-muted)] underline"
              onClick={() => {
                setTicket(null);
                setPreview(null);
                setCode("");
                setNotice(null);
              }}
            >
              Use a different email
            </button>
          ) : null}
        </form>
      </div>
    </main>
  );
}
